import { Checkbox, Stack, TextField, Typography } from '@mui/material'
import MaterialReceptionHeader from './MaterialReceptionHeader'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import AlertUI from '../generic/AlertUI'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useEffect, useState, useCallback } from 'react'
import { AddCircleOutlineRounded } from '@mui/icons-material'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { getMaterialReceptionColumns } from './MaterialReceptionExtendedColumnsUtil'
import { enumFilterOptions, systemValueCodeLabel, systemValueLabel } from '../generic/systemValues/systemValueLabel'
/**
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNoteDataAction} DeliveryNoteDataAction DataAction object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} DeliveryNote Data object
 */

/**
 *
 * @param {object} props
 * @param {DeliveryNote} props.deliveryNote
 * @param {React.Dispatch<DeliveryNoteDataAction>} props.dispatchDeliveryNote
 * @param {string} props.lastQrVersion
 * @returns
 */
const OrderSelector = ({ deliveryNote, dispatchDeliveryNote, lastQrVersion, rawMaterialExtendedColumns }) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const [AlertElement, showAlert] = AlertUI()
  const [AlertElementSupplier, showAlertSupplier] = AlertUI()
  const [currentSelectedOrders, setCurrentSelectedOrders] = useState([])
  const [lockedSupplier, setLockedSupplier] = useState(undefined)
  const [lockedOwner, setLockedOwner] = useState(undefined)
  const [lockedType, setLockedType] = useState(undefined)
  const [newSupplier, setNewSupplier] = useState({ code: null, name: null })
  const [editIndex, setEditIndex] = useState(-2)

  useEffect(() => {
    if (deliveryNote?.supplier) {
      setLockedSupplier({ id: deliveryNote.supplier.id, code: deliveryNote.supplier.code, name: deliveryNote.supplier.name })
    } else {
      setLockedSupplier(undefined)
    }
  }, [deliveryNote?.supplier])

  useEffect(() => {
    if (deliveryNote?.owner) {
      setLockedOwner({ id: deliveryNote.owner.id, code: deliveryNote.owner.category, name: deliveryNote.owner.value })
    } else {
      setLockedOwner(undefined)
    }
  }, [deliveryNote?.owner])

  useEffect(() => {
    if (deliveryNote?.type) {
      setLockedType(deliveryNote.type)
    } else {
      setLockedType(undefined)
    }
  }, [deliveryNote?.type])

  useEffect(() => {
    const selected = currentSelectedOrders?.[0]
    setLockedSupplier(selected
      ? (selected.supplier ? { id: selected.supplier.id, code: selected.supplier.code, name: selected.supplier.name } : null)
      : undefined
    )
    setLockedOwner(selected
      ? (selected.owner ? { id: selected.owner.id, code: selected.owner.category, name: selected.owner.value } : null)
      : undefined
    )
    setLockedType(selected ? selected.type : undefined)
  }, [currentSelectedOrders])

  const disableCheck = (row) => {
    if (!deliveryNote || !deliveryNote.recordCode) {
      return true
    }

    const find = deliveryNote.purchaseOrderTheoreticalReceptions?.find((reception) => reception.purchaseOrder.id === row.id)
    if (!find) {
      return false
    }

    if (find.status !== 'PENDING' && find.status !== 'THEORETICAL') {
      return true
    }

    return false
  }

  const checked = (row) => {
    if (!deliveryNote || !deliveryNote.recordCode) {
      return false
    }
    const found = currentSelectedOrders?.find((order) => order.purchaseOrderCode === row.purchaseOrderCode && order.lineOrderPosition === row.lineOrderPosition)
    return found !== undefined
  }

  const createNewReceptionFromOrder = (order) => {
    return {
      id: null,
      status: 'PENDING',
      recordCode: deliveryNote.recordCode,
      purchaseOrder: { id: order.id, name: order.purchaseOrderCode },
      lineOrderPosition: order.lineOrderPosition,
      rawMaterialReference: order.rawMaterialReference.name,
      revision: order.revisionCode,
      batch: 1,
      description: order.rawMaterialDescription,
      receivedTheoreticalQuantity: null,
      receivedQuantity: null,
      approvedQuantity: null,
      rejectedQuantity: null,
      updateDate: new Date(),
      deliveryNote: { id: deliveryNote.id }
    }
  }
  const formatPurcharseOrdersToPurchaseOrderReceptions = () => {
    const data = []
    currentSelectedOrders.forEach((order, index) => {
      const find = deliveryNote.purchaseOrderTheoreticalReceptions?.find((reception) => reception.purchaseOrder.name.toString() === order.purchaseOrderCode.toString() && reception.lineOrderPosition === order.lineOrderPosition)
      if (find) {
        data.push(find)
        return
      }

      const reception = createNewReceptionFromOrder(order)
      data.push(reception)
    })
    return data
  }

  const onSave = async (number, date) => {
    const data = { ...deliveryNote }
    data.date = date
    data.number = number
    data.purchaseOrderTheoreticalReceptions = formatPurcharseOrdersToPurchaseOrderReceptions()
    data.supplier = lockedSupplier
    data.owner = lockedOwner
    data.type = lockedType
    const result = await apiPut('deliveryNote/update/' + lastQrVersion + '/' + false, data)
    dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: result })
    return true
  }

  useEffect(() => {
    const getPurchaseOrder = async () => {
      const result = await apiGet('purchaseOrder/byDeliveryNoteId/' + deliveryNote.id)
      setCurrentSelectedOrders(result)
      return result
    }
    if (deliveryNote?.id) {
      getPurchaseOrder()
    } else {
      setCurrentSelectedOrders([])
    }
  }, [deliveryNote?.id])

  const addNewSupplier = () => {
    return (
      <>
        {AlertElementSupplier}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', padding: '1rem' }}>
          <TextField
            label={t_('Code')}
            value={newSupplier.code}
            onChange={(e) => setNewSupplier({ ...newSupplier, code: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label={t_('Name')}
            value={newSupplier.name}
            onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
            fullWidth
            required
          />
        </div>
      </>
    )
  }

  const saveNewSupplier = async () => {
    if (!newSupplier || !newSupplier.code || !newSupplier.name || newSupplier.code.trim() === '' || newSupplier.name.trim() === '') {
      showAlertSupplier({ title: t_('Please fill in all required fields'), severity: 'warning' })
      return false
    }
    try {
      await apiPost('supplier', newSupplier)
      setNewSupplier({ code: null, name: null })
      showAlertSupplier({ title: t_('Supplier added successfully'), severity: 'success' })
      return true
    } catch (error) {
      const backendMessage = error?.response?.data?.message || error?.response?.data || error.message
      showAlertSupplier({ severity: 'error', title: t_('Failed to add supplier'), message: t_(backendMessage) })
      return false
    }
  }

  const columns = [
    {
      name: '',
      field: '',
      search: false,
      render: (row) => {
        return (
          <Checkbox
            key={row.purchaseOrderCode + row.lineOrderPosition}
            color='primary'
            onChange={(e) => {
              const checked = e.target.checked
              const key = `${row.purchaseOrderCode}#${row.lineOrderPosition}`
              if (checked) {
                const currentSupplier = currentSelectedOrders[0]?.supplier?.id
                const currentOwner = currentSelectedOrders[0]?.owner?.id
                const currentType = currentSelectedOrders[0]?.type
                if (currentSupplier && row.supplier?.id !== currentSupplier) {
                  showAlert({ title: t_('You can only select orders from the same supplier'), severity: 'warning' })
                  return
                }
                if (currentOwner && row.owner?.id !== currentOwner) {
                  showAlert({ title: t_('You can only select orders from the same owner'), severity: 'warning' })
                  return
                }
                if (currentType && row.type !== currentType) {
                  showAlert({ title: t_('You can only select orders of the same type'), severity: 'warning' })
                  return
                }
                if (currentSelectedOrders.length === 0) {
                  setLockedSupplier(row.supplier)
                  setLockedOwner(row.owner)
                  setLockedType(row.type)
                }
                const next = [...currentSelectedOrders, row]
                setCurrentSelectedOrders(next)
                return
              }
              const next = currentSelectedOrders.filter(
                (o) => `${o.purchaseOrderCode}#${o.lineOrderPosition}` !== key
              )
              setCurrentSelectedOrders(next)
              if (next.length === 0) {
                setLockedSupplier(undefined)
                setLockedOwner(null)
                setLockedType(null)
              }
            }}
            checked={checked(row)}
            inputProps={{ 'aria-labelledby': '' }}
            disabled={disableCheck(row)}
          />
        )
      },
      readOnly: true
    },
    {
      name: t_('Status'),
      field: 'status',
      defaultNewValue: 'PENDING',
      hide: editIndex >= -1,
      render: (row) => systemValueLabel(t_, 'PurchaseOrderStatusEnum', row.status || ''),
      filterByLabel: { options: enumFilterOptions(t_, 'PurchaseOrderStatusEnum') }
    },
    {
      name: t_('Supplier code'),
      field: 'supplier.code',
      type: 'smartSelect',
      defaultNewValue: null,
      autoSelectFirst: false,
      edit: {
        source: async () => {
          return await apiGet('suppliersBasicData')
        },
        id: 'id',
        field: 'code',
        addButtonVisible: true,
        addDialogAccept: true,
        addDialogClose: true,
        addDialogTitle: {
          icon: <AddCircleOutlineRounded color='primary' sx={{ fontSize: '2rem' }} />,
          render: <Typography variant='h6'>{t_('Create new supplier')}</Typography>
        },
        content: addNewSupplier(),
        handleAddDialogAccept: async () => {
          const result = await saveNewSupplier()
          return result !== false
        }
      }
    },
    {
      name: t_('Supplier name'),
      field: 'supplier.name',
      hide: editIndex >= -1
    },
    {
      name: t_('Order'),
      field: 'purchaseOrderCode',
      mandatory: true
    },
    {
      name: t_('Pos / Line'),
      field: 'lineOrderPosition',
      numeric: true,
      mandatory: true
    },
    {
      name: t_('Serializable'),
      field: 'serializable',
      type: 'boolean'
    },
    {
      name: t_('Raw material reference'),
      field: 'rawMaterialReference.code',
      mandatory: true,
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('rawMaterialsReferenceIdCodeName')
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Revision'),
      field: 'revisionCode'
    },
    {
      name: t_('QR'),
      field: 'qr',
      charLimit: 10,
      defaultNewValue: '0000000000',
      condition: (row) => {
        const qr = String(row.qr ?? '').trim()
        if (qr === '' || /^[0-7]{10}$/.test(qr)) return undefined // Ten numeric digits from 0 to 7
        return t_('Must be 10 digits long, only numbers from 0 to 7')
      },
      mandatory: true
    },
    {
      name: t_('Project'),
      field: 'projectCode'
    },
    {
      name: t_('Description'),
      field: 'rawMaterialDescription',
      readOnly: true,
      hide: editIndex >= -1
    },
    {
      name: t_('Scheduled quantity'),
      field: 'orderedQuantity',
      numeric: true,
      mandatory: true
    },
    {
      name: t_('Remaining quantity'),
      field: 'retroOrder',
      numeric: true,
      mandatory: true
    },
    {
      name: t_('Owner'),
      field: 'owner.name',
      defaultNewValue: null,
      autoSelectFirst: false,
      mandatory: true,
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('selectableValues/OWNER')
        },
        id: 'id',
        field: 'name'
      }
    },
    {
      name: t_('Delivery date'),
      field: 'expectedDeliveryDate',
      type: 'dateTimePicker',
      mandatory: true,
      defaultNewValue: new Date().toISOString()
    },
    {
      name: t_('Type'),
      field: 'type',
      defaultNewValue: 'MANUAL',
      hide: editIndex >= -1,
      render: (row) => systemValueCodeLabel(t_, 'PurchaseOrderTypeEnum', row.type)
    }
  ]
  const getColumns = () =>
    getMaterialReceptionColumns({
      columns,
      extendedColumns: rawMaterialExtendedColumns,
      editIndex
    })

  const actions = {
    create: {
      fetchFunction: (row) => {
        const editedRow = { ...row }
        delete editedRow.name
        return apiPost('purchaseOrder', editedRow)
      },
      disabled: !isUserAuthorized('PURCHASE_ORDER_MANUAL_ADD')
    },
    edit: {
      fetchFunction: (row) => {
        const editedRow = { ...row }
        delete editedRow.name
        return apiPut('purchaseOrder', editedRow)
      },
      disabled: (row) => row.type === 'AUTOMATIC' || !isUserAuthorized('PURCHASE_ORDER_MANUAL_EDIT')
    }
  }

  const dataFetch = useCallback(async (pagination) => {
    const allUndefined = lockedSupplier === undefined && lockedOwner === undefined && lockedType === undefined
    if (allUndefined) {
      return apiPut('purchaseOrders/false', pagination)
    }
    const params = new URLSearchParams()

    if (lockedSupplier !== null && lockedSupplier !== undefined) { params.append('supplierId', lockedSupplier.id) }
    if (lockedOwner !== null && lockedOwner !== undefined) { params.append('ownerId', lockedOwner.id) }
    if (lockedType !== null && lockedType !== undefined) { params.append('type', lockedType) }

    return apiPut(`purchaseOrders/true?${params.toString()}`, pagination)
  }, [lockedSupplier, lockedOwner, lockedType])

  return (
    <Stack>
      {AlertElement}
      <MaterialReceptionHeader
        deliveryNote={deliveryNote}
        dispatchDeliveryNote={dispatchDeliveryNote}
        showAlert={showAlert}
        onSave={(number, date) => onSave(number, date)}
        window='ORDER_SELECTOR'
      />
      <div>
        <SmartMateriaUITable
          tableTuneUniqueKey='OrderSelector'
          actions={actions}
          columns={getColumns()}
          dataFetch={dataFetch}
          stickyTopOffset={112}
          pageable
          importFile={[{
            bean: {
              beanName: 'PurchaseOrderImportDto',
              beanPackage: 'com.smartpm.sun.usecase.purchaseorderimport.infrastructure.rest',
              serviceName: 'PurchaseOrderImportService',
              servicePackage: 'com.smartpm.sun.usecase.purchaseorderimport.application'
            },
            name: t_('Purchase order')
          }]}
          importFileDisabled={!isUserAuthorized('IMPORT_FILE_PURCHASE_ORDERS')}
          onEditIndexChanged={(editIndex) => setEditIndex(editIndex)}
        />
      </div>
    </Stack>
  )
}

export default OrderSelector
